import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ArrowLeft, Stethoscope, Clock, CheckCircle2, AlertCircle, Download, ExternalLink } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import ClientSidebar from '../Sidebar/ClientSidebar';
import ChatBox from '../Chat/ChatBox';
import { consultingAPI } from '../../api';

const ConsultingDetail = ({ user }) => {
  const { consultingId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [consulting, setConsulting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConsulting();
  }, [consultingId]);

  const loadConsulting = async () => {
    try {
      const data = await consultingAPI.getConsulting(consultingId);
      setConsulting(data);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de charger le consulting",
        variant: "destructive"
      });
      navigate('/client/consultings');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Terminé</Badge>;
      case 'in_progress':
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30"><Clock className="h-3 w-3 mr-1" />En cours</Badge>;
      case 'pending_payment':
        return <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30"><AlertCircle className="h-3 w-3 mr-1" />En attente de paiement</Badge>;
      default:
        return <Badge className="bg-gray-500/20 text-gray-500 border-gray-500/30"><AlertCircle className="h-3 w-3 mr-1" />En attente</Badge>;
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const blob = await consultingAPI.downloadFile(consultingId, fileId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de télécharger le fichier",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111]">
        <ClientSidebar />
        <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-[#A6A6A6]">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!consulting) return null;

  return (
    <div className="min-h-screen bg-[#111111]">
      <ClientSidebar />
      <div className="lg:ml-64 pt-24 pb-12 px-3 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <Button
            variant="ghost"
            className="mb-6 text-[#A6A6A6] hover:text-white"
            onClick={() => navigate('/client/consultings')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux consultings
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Consulting Details */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-white text-2xl mb-2">
                        <Stethoscope className="inline h-6 w-6 mr-2 text-[#C00000]" />
                        {consulting.consulting_id}
                      </CardTitle>
                      <p className="text-[#A6A6A6]">
                        {consulting.vehicle_info.brand} {consulting.vehicle_info.model} {consulting.vehicle_info.engine}
                      </p>
                    </div>
                    {getStatusBadge(consulting.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-white font-semibold mb-2">Informations du véhicule</h3>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        {consulting.vehicle_info.category && (
                          <div>
                            <span className="text-[#A6A6A6]">Catégorie :</span>
                            <p className="text-white">{consulting.vehicle_info.category}</p>
                          </div>
                        )}
                        <div>
                          <span className="text-[#A6A6A6]">Marque :</span>
                          <p className="text-white">{consulting.vehicle_info.brand}</p>
                        </div>
                        <div>
                          <span className="text-[#A6A6A6]">Modèle :</span>
                          <p className="text-white">{consulting.vehicle_info.model}</p>
                        </div>
                        {consulting.vehicle_info.year && (
                          <div>
                            <span className="text-[#A6A6A6]">Année :</span>
                            <p className="text-white">{consulting.vehicle_info.year}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.vin && (
                          <div>
                            <span className="text-[#A6A6A6]">N° de chassis :</span>
                            <p className="text-white font-mono">{consulting.vehicle_info.vin}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.license_plate && (
                          <div>
                            <span className="text-[#A6A6A6]">Immatriculation :</span>
                            <p className="text-white font-mono">{consulting.vehicle_info.license_plate}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.mileage && (
                          <div>
                            <span className="text-[#A6A6A6]">Kilométrage :</span>
                            <p className="text-white">{consulting.vehicle_info.mileage}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.engine && (
                          <div>
                            <span className="text-[#A6A6A6]">Motorisation :</span>
                            <p className="text-white">{consulting.vehicle_info.engine}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.engine_code && (
                          <div>
                            <span className="text-[#A6A6A6]">Code moteur :</span>
                            <p className="text-white">{consulting.vehicle_info.engine_code}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.power && (
                          <div>
                            <span className="text-[#A6A6A6]">Puissance :</span>
                            <p className="text-white">{consulting.vehicle_info.power} CV</p>
                          </div>
                        )}
                        {consulting.vehicle_info.fuel && (
                          <div>
                            <span className="text-[#A6A6A6]">Carburant :</span>
                            <p className="text-white">{consulting.vehicle_info.fuel}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.gearbox && (
                          <div>
                            <span className="text-[#A6A6A6]">Boîte :</span>
                            <p className="text-white">{consulting.vehicle_info.gearbox}</p>
                          </div>
                        )}
                        {consulting.vehicle_info.transmission && (
                          <div>
                            <span className="text-[#A6A6A6]">Transmission :</span>
                            <p className="text-white">{consulting.vehicle_info.transmission}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {(consulting.vehicle_info.ecu_type || consulting.vehicle_info.ecu_tool ||
                      consulting.vehicle_info.file_type || consulting.vehicle_info.read_method) && (
                      <div className="border-t border-[#A6A6A6]/20 pt-4">
                        <h3 className="text-white font-semibold mb-2">Calculateur & Outil</h3>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          {consulting.vehicle_info.ecu_type && (
                            <div>
                              <span className="text-[#A6A6A6]">Type ECU :</span>
                              <p className="text-white">{consulting.vehicle_info.ecu_type}</p>
                            </div>
                          )}
                          {consulting.vehicle_info.ecu_tool && (
                            <div>
                              <span className="text-[#A6A6A6]">Outil utilisé :</span>
                              <p className="text-white">{consulting.vehicle_info.ecu_tool}</p>
                            </div>
                          )}
                          {consulting.vehicle_info.file_type && (
                            <div>
                              <span className="text-[#A6A6A6]">Type fichier :</span>
                              <p className="text-white">{consulting.vehicle_info.file_type}</p>
                            </div>
                          )}
                          {consulting.vehicle_info.read_method && (
                            <div>
                              <span className="text-[#A6A6A6]">Méthode lecture :</span>
                              <p className="text-white">{consulting.vehicle_info.read_method}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {consulting.vehicle_info.hardware_mods && consulting.vehicle_info.hardware_mods.length > 0 && (
                      <div className="border-t border-[#A6A6A6]/20 pt-4">
                        <h3 className="text-white font-semibold mb-2">Modifications hardware</h3>
                        <div className="flex flex-wrap gap-2">
                          {consulting.vehicle_info.hardware_mods.map((mod, i) => (
                            <Badge key={i} variant="outline" className="border-[#C00000]/50 text-white text-xs">
                              {mod}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="border-t border-[#A6A6A6]/20 pt-4">
                      <h3 className="text-white font-semibold mb-2">Problème décrit</h3>
                      <p className="text-[#A6A6A6] text-sm whitespace-pre-wrap">{consulting.problem}</p>
                    </div>

                    {(consulting.files?.length > 0 || consulting.links?.length > 0) && (
                      <div className="border-t border-[#A6A6A6]/20 pt-4">
                        <h3 className="text-white font-semibold mb-3">Fichiers et liens</h3>
                        <div className="space-y-2">
                          {consulting.files?.map((file) => (
                            <div key={file.id} className="flex items-center justify-between bg-[#111111] p-3 rounded-lg">
                              <span className="text-white text-sm">{file.filename}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-[#C00000] hover:text-[#C00000]/80"
                                onClick={() => handleDownload(file.id, file.filename)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          {consulting.links?.map((link) => (
                            <div key={link.id} className="flex items-center justify-between bg-[#111111] p-3 rounded-lg">
                              <span className="text-white text-sm truncate">{link.url}</span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-[#C00000] hover:text-[#C00000]/80"
                                onClick={() => window.open(link.url, '_blank')}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="border-t border-[#A6A6A6]/20 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-[#A6A6A6]">Prix du consulting</span>
                        <span className="text-white font-bold text-xl">{consulting.price}€</span>
                      </div>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-[#A6A6A6]">Statut paiement</span>
                        <Badge className={consulting.payment_status === 'paid' ? 'bg-green-500/20 text-green-500' : 'bg-orange-500/20 text-orange-500'}>
                          {consulting.payment_status === 'paid' ? 'Payé' : 'En attente'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column - Chat */}
            <div className="lg:col-span-1">
              <ChatBox 
                orderId={consultingId}
                user={user}
                isConsulting={true}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultingDetail;
